// libraries
import React, { Suspense, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import store, { RootState } from "store";

//  css, library components
import ButtonSmall from "modules/Common/Components/ButtonSmall";

// components
// import Message from "modules/Common/Components/Message";
import CreateAsset from "../CampaignAssets/Components/CreateAsset";

// utils, actions
import { getDesignHTMLService, resetDesignPreview } from "modules/Design/action";
import { showLoader } from "modules/Design/Scripts/DynamicStripo";
import { getQueryCampaignId, getQueryEmailId } from "modules/EmailBrowser/utils";
import { closeCurationSidebar } from "modules/GlobalActions";
import { getDefaultTemplateStyleId } from "utils";

// types, constants
import { DESIGN } from "constants/actionTypes";
import { TAB_EDITION } from "constants/paths";
import { getEditionArticles, resetEditionData, setEditionEmailId, updateEmailCampaignState } from "./actions";
import EditionPageHeading from "modules/Common/Components/EditionPageHeading";
import { TEMP_VERSION_NAME } from "constants/index";
import { updateEmailNameForBrowserEmailList } from "modules/EmailBrowser/actions";


const EditionSmartSourcedContent = React.lazy(() => import("./EditionSmartSourcedContent"));
const FavoritesTab = React.lazy(() => import("modules/Common/Components/FavoritesTab"));
const Publish = React.lazy(() => import("modules/Publish/Components/Publish"));
const Design = React.lazy(() => import("modules/Design/Shared/Design"));
const EditionEdit = React.lazy(() => import("./EditionEditV2"));

interface Props {
  tab?: string;
}

const EditionEditWrapperV2 = (props: Props) => {
  const { tab } = props;
  const location = useLocation();
  const history = useHistory();
  const isSource = tab !== "source";

  // get campaignId from url
  let queryCampaignId = getQueryCampaignId();
  let queryEmailId = getQueryEmailId();

  // define go back url
  let goBackUrl = queryCampaignId ? `/campaigns/content?campaign_id=${queryCampaignId}` : "/campaigns";

  // local state
  // const [showDesignBtn, setShowDesignBtn] = useState(false);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [isEmailWithoutCampaign, setIsEmailWithoutCampaign] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");
  const [redirectToPublish, setRedirectToPublish] = useState(false);
  const [toggleDisabled, setToggleDisabled] = useState(true);
  const [firstTimeLoad, setFirstTimeLoad] = useState(true);
  const [publishLoader, setPublishLoader] = useState(false);
  const [previewLoader, setPreviewLoader] = useState(false);
  const [togglePreview, setTogglePreview] = useState(false);
  const [showDesignBtn, setShowDesignBtn] = useState(false);
  const [prevArticlesOrder, setPrevArticlesOrder] = useState([]);
  const [articlesOrder, setArticlesOrder] = useState([]);

  // data from redux store
  const noCampaign: boolean = useSelector((state: RootState) => state.editionData.noCampaign);
  const previousEmailId: number = useSelector((state: RootState) => state.editionData.emailId);
  const curatedCount: number = useSelector((state: RootState) => state.editionData.curatedCount);
  const campaignId: number = useSelector((state: RootState) => state.editionData.assetId);
  const emailType: number = useSelector((state: RootState) => state.editionData.emailType);
  const editionStatus: string = useSelector((state: RootState) => state.editionData.editionStatus);
  const editionName: string = useSelector((state: RootState) => state.editionData.editionName);
  const editionArticlesLoading: boolean = useSelector((state: RootState) => state.editionData.editionArticlesLoading);
  const editionNameChangeLoading: boolean = useSelector((state: RootState) => state.editionData.nameChange.loading);

  // show preview for email design
  const showPreview = (html_content: any, email_content: string, css_content: any) => {
    if (email_content === undefined && html_content?.html) {
      store.dispatch({
        type: DESIGN.UPDATE_HTML,
        email_html: html_content?.email_html,
        html: html_content?.html,
        css: css_content?.css,
      });
    } else {
      store.dispatch({
        type: DESIGN.UPDATE_HTML,
        email_html: email_content,
        html: html_content,
        css: css_content,
      });
    }
    store.dispatch({
      type: DESIGN.LOAD,
      loader: false,
    });
    showLoader(false);
    if (!firstTimeLoad) setTogglePreview(true);
  };

  // first render useEffect
  useEffect(() => {
    //! need to check if this can be implemented with previous email id
    // resetting design data for the very first time and then getting it back on the first load
    resetDesignPreview();
    if (!window.location.pathname.includes("publish") && !window.location.pathname.includes("design"))
      getDesignHTMLService(getQueryEmailId(), getDefaultTemplateStyleId(), 0, showPreview, false);
    closeCurationSidebar();
    if (previousEmailId?.toString() !== queryEmailId?.toString()) {
      resetEditionData();
    }
    setFirstTimeLoad(false);
  }, []);

  // if campaign id is present in url, then set isEmailWithoutCampaign to false
  useEffect(() => {
    if (queryCampaignId && isEmailWithoutCampaign) {
      setIsEmailWithoutCampaign(false);
      updateEmailCampaignState();
    } if (noCampaign && !isEmailWithoutCampaign) {
      setIsEmailWithoutCampaign(true);
    }
  }, [queryCampaignId, isEmailWithoutCampaign, noCampaign]);

  useEffect(() => {
    if (queryEmailId.toString() !== previousEmailId.toString()) {
      setEditionEmailId(queryEmailId);
    }
    // if(!firstTimeLoad) {
    getEditionArticles(queryEmailId)
    // }
  }, [queryEmailId, previousEmailId])

  // functions  
  const nameChangeHandler = (name: string) => {
    updateEmailNameForBrowserEmailList(queryEmailId, name);
  }


  // save to campaign in case of Email without campaign
  const savetoCampaign = (value: boolean, url: string) => {
    setShowCreateCampaign(value);
    setRedirectUrl(url);
  };

  // edit design button click handler for email without campaig
  const EditDesignAction = (
    <div className="d-inline-flex">
      <CreateAsset
        noDropdown={true}
        noPortal={true}
        updateAsset={true}
        assetId={queryEmailId?.toString()}
        name={editionName}
        noRedirection={false}
        redirectURL={redirectUrl}
        callbackMethod={setIsEmailWithoutCampaign}
        className="hide-mac-article"
      >
        <span className="button-small-spacing">
          <ButtonSmall
            className={tab === "design" ? "styling-tag-green" : "styling-tag-grey"}
            secondary
            value="Edit Design"
            onClick={() => {
              store.dispatch({
                type: DESIGN.RESET,
              });
              savetoCampaign(true, "/campaigns/editionedit/design/" + window.location.search);
            }}
          />
        </span>
      </CreateAsset>
    </div>
  );



  // showing subscription message
  const subHeading: { [key: string]: string } = {
    "0": "Digest",
    "1": "Ad hoc",
    "-1": ''
  };

  // main heading/name/subject of the email
  const mainHeading: string = editionArticlesLoading
    ? ""
    : !editionName.includes(TEMP_VERSION_NAME)
      ? editionName
      : editionStatus === "Sent"
        ? "[Sent] " + { editionName }
        : "Edition Draft"

  return (
    <>
      <Suspense fallback={<></>}>
        <EditionPageHeading
          toggleDisabled={toggleDisabled}
          setToggleDisabled={setToggleDisabled}
          publishLoader={publishLoader}
          setPublishLoader={setPublishLoader}
          setRedirectToPublish={setRedirectToPublish}
          previewLoader={previewLoader}
          setPreviewLoader={setPreviewLoader}
          togglePreview={togglePreview}
          setTogglePreview={setTogglePreview}
          showPreview={showPreview}
          headingSmallGreen={subHeading[emailType?.toString()]}
          emailType={emailType}
          headingMain={mainHeading}
          editionArticlesLoading={editionArticlesLoading}
          nameChangeHandler={nameChangeHandler}
          itemNameLoading={editionNameChangeLoading}
          curatedCount={curatedCount}
          showDesignBtn={showDesignBtn}
          editionStatus={editionStatus}
          assetId={campaignId}
          campaignId={campaignId}
          tab={tab}
          page={"editions"}
          isEmailWithoutCampaign={isEmailWithoutCampaign}
          overlayClass={"asset-header"}
          goBackURL={goBackUrl}
          editDesignAction={EditDesignAction}
          enableStickyHeader={true}
          prevArticlesOrder={prevArticlesOrder}
          articlesOrder={articlesOrder}
          setIsEmailWithoutCampaign={setIsEmailWithoutCampaign}
        />
      </Suspense>

      {/* 
        show message box for save here
      */}
      {/* <Suspense fallback={<>Loading Message ...</>}>
        {isEmailWithoutCampaign && (
          <div className="pe-5 me-4">
            <Message
              message={ALERT_MESSAGES.DEFAULT_EMAIL_WITHOUT_CAMPAIGN}
              label="Heads up!"
              type="info"
              marginTop="20"
              callToAction={() => {
                return (
                  <CreateAsset
                    noDropdown={true}
                    noPortal={true}
                    updateAsset={true}
                    assetId={queryEmailId.toString()}
                    name={emailName}
                    noRedirection={true}
                    callbackMethod={setIsEmailWithoutCampaign}
                    hideDynamic={true}
                    updateURL={true}
                    saveonPublish={false}
                  >
                    <ButtonSmall value={"Save"} />
                  </CreateAsset>
                );
              }}
            />
          </div>
        )}
      </Suspense> */}

      {tab === TAB_EDITION.SOURCE ? (
        <Suspense fallback={<>loading...</>}>
          <EditionSmartSourcedContent isAssetWithoutCampaign={isEmailWithoutCampaign} />
        </Suspense>
      ) : tab === TAB_EDITION.FAVORITES ? (
        <Suspense fallback={<>loading...</>}>
          <FavoritesTab isEdition />
        </Suspense>
      ) : tab === TAB_EDITION.PUBLISH ? (
        <Suspense fallback={<>loading...</>}>
          <Publish />
        </Suspense>
      ) : tab === TAB_EDITION.DESIGN ? (
        <Suspense fallback={<>loading...</>}>
          <Design
            redirectToPublish={redirectToPublish}
            setRedirectToPublish={setRedirectToPublish}
            toggleDisabled={toggleDisabled}
            setToggleDisabled={setToggleDisabled}
          />
        </Suspense>
      ) : (
        // <></>
        <Suspense fallback={<></>}>
          <EditionEdit
            setShowDesignBtn={setShowDesignBtn}
            setIsEmailWithoutCampaign={setIsEmailWithoutCampaign}
            isEmailWithoutCampaign={isEmailWithoutCampaign}
            savetoCampaign={savetoCampaign}
            redirectUrl={redirectUrl}
            showCreateCampaign={showCreateCampaign}
            setPrevArticlesOrder={setPrevArticlesOrder}
            setArticlesOrder={setArticlesOrder}
            previewLoader={previewLoader}
            setPreviewLoader={setPreviewLoader}
            isSource={isSource}
          />
        </Suspense>
      )}
    </>
  )
};

export default EditionEditWrapperV2;
